/*
 * newsbot - Batch Status Settings
 * Forked from autobrr dashboard patterns
 */

import { useBatchStatus, useDashboardStats } from "@api/queries";
import { classNames } from "@utils";
import type { BatchDetail } from "@/types";
import { formatDistanceToNowStrict } from "date-fns";
import { StatsItem } from "@components/StatsItem";

interface StatusCellMapEntry {
  colors: string;
  label: string;
}

const StatusCellMap: Record<string, StatusCellMapEntry> = {
  in_progress: {
    colors: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    label: "in progress"
  },
  ended: {
    colors: "bg-green-175 text-green-900 dark:bg-green-900 dark:text-green-200",
    label: "ended"
  },
  failed: {
    colors: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    label: "failed"
  },
  canceling: {
    colors: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    label: "canceling"
  },
  canceled: {
    colors: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    label: "canceled"
  }
};

const StatusBadge = ({ status }: { status: string }) => {
  const entry = StatusCellMap[status] || { colors: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300", label: status };
  return (
    <span className={classNames(entry.colors, "inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium")}>
      {entry.label}
    </span>
  );
};

export default function BatchStatus() {
  const { data, isLoading, error } = useBatchStatus(true);
  const {
    data: dashboardStats,
    isLoading: dashboardStatsLoading,
    isError: dashboardStatsError,
  } = useDashboardStats();

  if (error) {
    return (
      <div className="divide-y divide-gray-200 dark:divide-gray-700 lg:col-span-9">
        <div className="py-6 px-4 sm:p-6 lg:pb-8">
          <div className="p-4 text-red-500">
            Error loading batch status: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700 lg:col-span-9">
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Batch Processing Status
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor batch processing across extraction, categorization, ranking, and suggestions.
          </p>
        </div>

        {/* Stats */}
        <dl className={classNames(
          "grid grid-cols-2 gap-2 sm:gap-5 mt-5 sm:grid-cols-2 lg:grid-cols-4",
          isLoading ? "animate-pulse" : ""
        )}>
          <StatsItem name="Pending Batches" value={data?.summary.pending_batches ?? 0} />
          <StatsItem name="Processing" value={data?.summary.items_processing ?? 0} />
          <StatsItem name="Completed" value={data?.summary.items_completed ?? 0} />
          <StatsItem name="Errored" value={data?.summary.items_errored ?? 0} />
        </dl>

        {/* Article Stats */}
        <div className="mt-8">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            Article Extraction Status
          </h3>
          <dl className="grid grid-cols-2 gap-2 sm:gap-5 mt-3 sm:grid-cols-4">
            <StatsItem name="Pending" value={data?.articles.pending ?? 0} />
            <StatsItem name="Processing" value={data?.articles.processing ?? 0} />
            <StatsItem name="Completed" value={data?.articles.completed ?? 0} />
            <StatsItem name="Failed" value={data?.articles.failed ?? 0} />
          </dl>
          {dashboardStatsLoading ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" role="status">
              Loading extraction failure details…
            </p>
          ) : dashboardStatsError ? (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
              Extraction failure details are unavailable.
            </p>
          ) : (
            <div className="mt-4 rounded-md bg-gray-50 p-4 text-sm dark:bg-gray-850">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                Failed articles: {dashboardStats?.extraction_retryable ?? 0} retryable · {dashboardStats?.extraction_permanently_failed ?? 0} permanent
              </p>
              <ul className="mt-2 grid gap-1 text-gray-600 dark:text-gray-400 sm:grid-cols-2">
                {Object.entries(dashboardStats?.extraction_failed_breakdown ?? {})
                  .sort(([, left], [, right]) => right - left)
                  .map(([reason, count]) => (
                    <li key={reason}>{reason.replaceAll("_", " ")}: {count}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>

        {/* Batch Table */}
        <div className="flex flex-col mt-8">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            Active Batches
          </h3>

          {(!data?.batches || data.batches.length === 0) ? (
            <div className="mt-4 mb-2 bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md overflow-auto">
              <div className="flex items-center justify-center py-16">
                <p className="text-gray-500 dark:text-gray-400">No active batches</p>
              </div>
            </div>
          ) : (
            <div className="inline-block min-w-full mt-4 mb-2 align-middle">
              <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md overflow-auto">
                <table className="min-w-full rounded-md divide-y divide-gray-200 dark:divide-gray-750">
                  <thead className="bg-gray-100 dark:bg-gray-850">
                    <tr>
                      <th scope="col" className="first:pl-5 first:rounded-tl-md pl-3 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Operation
                      </th>
                      <th scope="col" className="pl-3 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Status
                      </th>
                      <th scope="col" className="pl-3 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Progress
                      </th>
                      <th scope="col" className="last:rounded-tr-md pl-3 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Age
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-150 dark:divide-gray-750">
                    {data.batches.map((batch: BatchDetail) => (
                      <tr key={batch.batch_id}>
                        <td className="first:pl-5 pl-3 pr-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-300">
                          {batch.operation}
                        </td>
                        <td className="pl-3 pr-3 py-3 whitespace-nowrap">
                          <StatusBadge status={batch.status} />
                        </td>
                        <td className="pl-3 pr-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {batch.succeeded}/{batch.items}
                          {batch.errored > 0 && (
                            <span className="text-red-500 ml-1">({batch.errored} errors)</span>
                          )}
                        </td>
                        <td className="pl-3 pr-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDistanceToNowStrict(new Date(batch.created_at), { addSuffix: false })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Last updated */}
        {data?.generated_at && (
          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            Last updated: {new Date(data.generated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
